// Contenu d'aide pour SuiviPlus - Gestion de stock

export const HELP_CONTENT = {
  // === GESTION DES STOCKS ===
  stockActuel: {
    title: "Stock actuel",
    explanation: "Le nombre d'unités actuellement disponibles en stock pour ce produit. Cette valeur est mise à jour automatiquement à chaque mouvement de stock (ajout ou retrait).",
    example: "Si vous avez 12 boîtes de lait infantile en stock, le stock actuel est de 12.",
    notes: [
      "Vérifié régulièrement pour éviter les ruptures",
      "Mis à jour automatiquement lors des commandes",
      "Affiché avec un code couleur (vert/orange/rouge)"
    ]
  },

  stockIdeal: {
    title: "Stock idéal",
    explanation: "Le niveau de stock que vous souhaitez maintenir pour ce produit. Lorsque le stock actuel descend en dessous de cette valeur, une alerte est générée pour vous prévenir qu'il faut recommander.",
    example: "Pour du lait infantile consommé régulièrement, vous pouvez fixer un stock idéal de 20 boîtes pour éviter les ruptures.",
    notes: [
      "À définir selon la consommation et le délai de livraison",
      "Déclenche automatiquement une alerte si stock < idéal",
      "Peut être ajusté à tout moment selon les besoins"
    ]
  },

  alerteStockBas: {
    title: "Alerte stock bas",
    formula: "Stock actuel < Stock idéal ET Stock actuel > 0",
    explanation: "Une alerte est générée lorsque le stock actuel est inférieur au stock idéal mais qu'il reste encore des unités disponibles. Cela vous permet de passer commande avant la rupture.",
    example: "Stock actuel : 3 boîtes\nStock idéal : 20 boîtes\n→ Alerte stock bas (il reste 3 boîtes mais il faut recommander 17 boîtes)",
    notes: [
      "Affichée avec un point orange",
      "Visible dans le badge de notifications",
      "Permet d'anticiper les ruptures"
    ]
  },

  ruptureStock: {
    title: "Rupture de stock",
    formula: "Stock actuel = 0",
    explanation: "Le produit n'est plus disponible en stock. C'est une situation critique qui nécessite une commande urgente pour éviter une pénurie.",
    example: "Stock actuel : 0 boîte\n→ Rupture de stock critique !",
    notes: [
      "Affichée avec un point rouge",
      "Priorité haute dans la liste de courses",
      "Notification push envoyée (si activées)"
    ]
  },

  listeDeCourses: {
    title: "Liste de courses",
    formula: "Quantité à commander = Stock idéal - Stock actuel",
    explanation: "Liste automatique de tous les produits dont le stock est inférieur au stock idéal, avec la quantité recommandée pour atteindre le niveau optimal.",
    example: "Produit : Lait infantile\nStock actuel : 3\nStock idéal : 20\n→ À commander : 17 boîtes",
    notes: [
      "Mise à jour automatiquement",
      "Triée par priorité (ruptures en premier)",
      "Exportable en CSV pour envoi aux fournisseurs"
    ]
  },

  // === GESTION DES PRIX ===
  prixActuel: {
    title: "Prix actuel",
    explanation: "Le dernier prix d'achat enregistré pour ce produit. Il est automatiquement mis à jour lorsque vous enregistrez une nouvelle commande avec un prix différent.",
    example: "Si vous commandez à 10,50€ puis à 11,00€, le prix actuel sera de 11,00€.",
    notes: [
      "Mis à jour automatiquement lors des commandes",
      "Historique complet conservé",
      "Utilisé pour calculer la valeur du stock"
    ]
  },

  historiquePrix: {
    title: "Historique des prix",
    explanation: "Suivi complet de l'évolution des prix d'un produit au fil du temps. Permet de détecter les hausses, baisses et de comparer les fournisseurs.",
    example: "01/11/2025 : 10,00€ (Fournisseur A)\n15/11/2025 : 10,50€ (Fournisseur A)\n01/12/2025 : 9,80€ (Fournisseur B)\n→ Fournisseur B est moins cher !",
    notes: [
      "Affiche min, max et moyenne",
      "Indicateurs de hausse/baisse",
      "Permet de négocier avec les fournisseurs"
    ]
  },

  commandeAutomatique: {
    title: "Commande automatique",
    formula: "Nouveau stock = Stock actuel + Quantité commandée",
    explanation: "Lorsque vous enregistrez une commande avec une quantité, le stock est automatiquement incrémenté et une entrée est créée dans l'historique des mouvements.",
    example: "Stock actuel : 5\nCommande : 10 boîtes\n→ Nouveau stock : 15 (mise à jour automatique)",
    notes: [
      "Plus besoin d'ajouter manuellement au stock",
      "Historique complet avec traçabilité",
      "Note générée : 'Réception commande - Fournisseur - Qty × Prix'"
    ]
  },

  // === FOURNISSEURS ===
  fournisseur: {
    title: "Fournisseur",
    explanation: "L'entreprise ou le commerce auprès duquel vous achetez vos produits. Chaque produit peut être associé à un fournisseur principal, et l'historique des prix permet de comparer plusieurs fournisseurs.",
    example: "Fournisseur A : Lait à 10,50€ (délai 2 jours)\nFournisseur B : Lait à 9,80€ (délai 5 jours)\n→ Choix selon prix vs délai",
    notes: [
      "Informations complètes (contact, site web, email, téléphone)",
      "Comparaison automatique des prix",
      "Statistiques par fournisseur dans le dashboard"
    ]
  },

  // === MOUVEMENTS DE STOCK ===
  mouvementStock: {
    title: "Mouvement de stock",
    explanation: "Toute modification du stock (ajout ou retrait) est enregistrée avec la date, la personne, la quantité et une note explicative. Cet historique permet une traçabilité complète.",
    example: "+10 boîtes - Marine D. - 22/12/2025 10:30\nNote : Réception commande Fournisseur A\n\n-2 boîtes - Jérôme D. - 22/12/2025 14:15\nNote : Utilisation pour groupe bébés",
    notes: [
      "Traçabilité complète (qui, quand, combien, pourquoi)",
      "Affiché dans l'historique du produit",
      "Exportable en CSV pour analyse"
    ]
  },

  personneSelectionnee: {
    title: "Personne sélectionnée",
    explanation: "Pour assurer la traçabilité, vous devez sélectionner la personne qui effectue l'action (ajout/retrait de stock, commande). Cette information est enregistrée dans l'historique.",
    example: "Si Marine ajoute 10 boîtes au stock, l'historique affichera :\n'Marine D. a ajouté +10 le 22/12/2025'",
    notes: [
      "Sélection persistante entre les pages",
      "Affichage des initiales dans l'historique",
      "Permet de savoir qui a fait quoi"
    ]
  },

  // === CATÉGORIES ===
  categorie: {
    title: "Catégorie",
    explanation: "Permet de regrouper les produits similaires pour faciliter le tri et le filtrage. Par exemple : 'Lait infantile', 'Couches', 'Produits d'entretien', etc.",
    example: "Catégorie : Lait infantile\nProduits : Guigoz 1, Gallia 1, Nidal 1\n→ Facilite la recherche et le suivi",
    notes: [
      "Créez vos propres catégories",
      "Filtrage rapide dans la liste des produits",
      "Statistiques par catégorie dans le dashboard"
    ]
  },

  // === DASHBOARD ===
  valeurStock: {
    title: "Valeur totale du stock",
    formula: "Somme de (Stock actuel × Prix actuel) pour tous les produits",
    explanation: "La valeur totale de tous vos produits en stock, calculée en multipliant le stock actuel par le prix actuel de chaque produit.",
    example: "Produit A : 10 unités × 5€ = 50€\nProduit B : 5 unités × 20€ = 100€\nValeur totale = 150€",
    notes: [
      "Mise à jour en temps réel",
      "Basée sur les prix actuels (derniers enregistrés)",
      "Affichée dans le dashboard"
    ]
  },

  prixMoyen: {
    title: "Prix moyen",
    formula: "Somme des prix actuels ÷ Nombre de produits",
    explanation: "Le prix moyen de tous vos produits en stock. Donne une idée du coût moyen par produit.",
    example: "Produit A : 5€\nProduit B : 20€\nProduit C : 8€\nPrix moyen = (5 + 20 + 8) ÷ 3 = 11€",
    notes: [
      "Statistique indicative",
      "Varie selon les types de produits",
      "Affichée dans le dashboard"
    ]
  },

  tendancePrix: {
    title: "Tendance des prix",
    formula: "(Prix actuel - Prix il y a 90 jours) ÷ Prix il y a 90 jours × 100",
    explanation: "Évolution du prix d'un produit sur les 90 derniers jours. Permet de détecter rapidement les hausses ou baisses significatives.",
    example: "Prix il y a 90j : 10€\nPrix actuel : 11€\nTendance = +10% (hausse)",
    notes: [
      "Indicateur rouge si hausse > 10%",
      "Indicateur vert si baisse",
      "Gris si stable (variation < 5%)"
    ]
  },

  comparaisonFournisseurs: {
    title: "Comparaison fournisseurs",
    explanation: "Compare automatiquement les fournisseurs selon le nombre de produits, les prix moyens, le total des achats et la date du dernier achat. Aide à identifier les meilleurs partenaires.",
    example: "Fournisseur A :\n- 15 produits\n- Prix moyen : 8,50€\n- Total achats : 3 500€\n- Dernier achat : hier\n\nFournisseur B :\n- 8 produits\n- Prix moyen : 12€\n- Total achats : 1 200€\n- Dernier achat : il y a 2 mois",
    notes: [
      "Aide à identifier les fournisseurs principaux",
      "Permet de diversifier ou regrouper les commandes",
      "Historique complet disponible"
    ]
  },

  suggestionCommande: {
    title: "Suggestions de commande",
    explanation: "Recommandations automatiques basées sur les stocks bas et ruptures, avec calcul du coût estimé et priorisation (haute/moyenne/basse) selon l'urgence.",
    example: "Priorité HAUTE (rupture) :\nLait Guigoz 1 - 0/20 → Commander 20 × 10,50€ = 210€\n\nPriorité MOYENNE (stock bas) :\nCouches T3 - 5/30 → Commander 25 × 8€ = 200€",
    notes: [
      "Mise à jour automatique",
      "Triée par priorité",
      "Coût total estimé affiché"
    ]
  },

  // === NOTIFICATIONS ===
  notificationsPush: {
    title: "Notifications push",
    explanation: "Alertes en temps réel envoyées sur vos appareils (ordinateur, smartphone) lorsqu'un événement important se produit : rupture de stock, stock bas, etc.",
    example: "🔔 Rupture de stock !\nLait Guigoz 1 : stock à 0\n→ Commande urgente recommandée",
    notes: [
      "Activables depuis n'importe quel appareil",
      "Persistantes (survivent aux redémarrages serveur)",
      "Multi-device (ordinateur + téléphone)",
      "Désactivables à tout moment"
    ]
  },

  // === EXPORTS ===
  exportCSV: {
    title: "Export CSV",
    explanation: "Export de vos données au format CSV (compatible Excel) pour analyse externe, archivage ou partage avec des collaborateurs.",
    example: "4 types d'exports disponibles :\n1. Tous les produits\n2. Alertes uniquement\n3. Historique des mouvements\n4. Historique des prix",
    notes: [
      "Format UTF-8 compatible Excel",
      "Filtres disponibles (dates, produit, fournisseur)",
      "Nom de fichier avec horodatage",
      "Accessible depuis le Dashboard"
    ]
  }
};

// Catégories pour organisation
export const HELP_CATEGORIES = {
  stocks: {
    title: "📦 Gestion des stocks",
    keys: ['stockActuel', 'stockIdeal', 'alerteStockBas', 'ruptureStock', 'listeDeCourses']
  },
  prix: {
    title: "💰 Gestion des prix",
    keys: ['prixActuel', 'historiquePrix', 'commandeAutomatique']
  },
  fournisseurs: {
    title: "🏢 Fournisseurs",
    keys: ['fournisseur', 'comparaisonFournisseurs']
  },
  mouvements: {
    title: "📝 Mouvements et traçabilité",
    keys: ['mouvementStock', 'personneSelectionnee']
  },
  organisation: {
    title: "🗂️ Organisation",
    keys: ['categorie']
  },
  dashboard: {
    title: "📊 Dashboard et statistiques",
    keys: ['valeurStock', 'prixMoyen', 'tendancePrix', 'suggestionCommande']
  },
  notifications: {
    title: "🔔 Notifications",
    keys: ['notificationsPush']
  },
  exports: {
    title: "📤 Exports",
    keys: ['exportCSV']
  }
};
